import { useId, useState } from 'react'
import type { ChangeEvent, MouseEvent } from 'react'
import { AppstoreOutlined, DeleteOutlined } from '../../../components/icons'
import { Checkbox } from '../../../components/ui/Checkbox'
import { Chip } from '../../../components/ui/Chip'
import { cx } from '../../../components/ui/cx'
import type { FileToResolve, IdentifiedPart, StagingRowSignal } from '../staging-types'
import {
  ANALYSIS_SIGNAL_REASON_LABEL,
  PART_TYPE_TONE,
  STAGED_PART_TYPE_LABEL,
  stagingRowSignalTintClass,
} from '../staging-types'
import { DrawingAssociationControl } from './DrawingAssociationControl'

export interface IdentifiedPartRowProps {
  readonly part: IdentifiedPart
  /** Candidate drawings to associate — the only "files" surface the
   * `StagingService` seam exposes (there is no separate drawing-catalog
   * lookup); sourced from the session's files-to-resolve list. */
  readonly candidateDrawings: readonly FileToResolve[]
  readonly onAssociateDrawing: (fileId: string) => void
  readonly onGenerateDrawing: () => Promise<unknown>
  /** UID-222 #9-upload — real presign-then-PUT flow; takes the chosen File. */
  readonly onUploadDrawing: (file: File) => Promise<unknown>
  readonly onQuantityChange: (quantity: number) => void
  /** UID-222 #7 — remove this identified part (real, `DELETE .../parts/:partId`). */
  readonly onRemove: () => void
  /** ADR-0130 (UID-425 Bug 2) — bulk-remove row selection, restored from
   * ADR-0124 D1's removal (that removal targeted the "Configure selected"
   * use of the checkbox; the design's checkbox is a separate bulk-REMOVE
   * selector D1 never examined). Selection has NO effect on the primary CTA
   * (ADR-0124 D2 — see `staging-cta.ts`). */
  readonly selected: boolean
  readonly onToggleSelect: (event: MouseEvent<HTMLInputElement>) => void
  /** UID-255 (additive/optional) — this row's auto-quote-vs-manual signal,
   * looked up by `part.partId`. Absent ⇒ no tint/chip (defensive fallback). */
  readonly analysisSignal?: StagingRowSignal | undefined
}

/**
 * Identified-part row (S-D, UID-188/UID-222, `Fictiv Platform.dc.html:2692-
 * 2816`, checkbox restored per ADR-0130/`:3284`): checkbox, thumbnail
 * (graceful placeholder when `thumbnailUrl` is null), name + CAD filename
 * line, associated-drawing dropdown, part-type badge, a derived
 * configuration-status chip, a plain numeric Qty input, and a real remove
 * affordance.
 *
 * UID-402 — the "Set material…" chip is GONE (ADR-0124 D1): it was a
 * configuration affordance, which this page must not offer.
 */
export function IdentifiedPartRow({
  part,
  candidateDrawings,
  onAssociateDrawing,
  onGenerateDrawing,
  onUploadDrawing,
  onQuantityChange,
  onRemove,
  selected,
  onToggleSelect,
  analysisSignal,
}: IdentifiedPartRowProps) {
  const [qtyDraft, setQtyDraft] = useState(String(part.quantity))
  const fileInputId = useId()
  // UID-402 — shared helper, so this row cannot drift from the matched/file
  // rows on what earns a wash. An identified part that needs configuration no
  // longer gets the orange wash (only its Chip says so).
  const signalTintClass = stagingRowSignalTintClass(analysisSignal)

  function commitQuantity() {
    const parsed = Number(qtyDraft)
    if (Number.isFinite(parsed) && parsed > 0 && parsed !== part.quantity) {
      onQuantityChange(Math.floor(parsed))
    } else {
      setQtyDraft(String(part.quantity))
    }
  }

  function handleQtyChange(event: ChangeEvent<HTMLInputElement>) {
    setQtyDraft(event.target.value)
  }

  return (
    <tr className={cx('staging-row', signalTintClass)}>
      <td>
        {/* UID-600 (bug follow-up) — migrated from a raw, unwrapped
            `<input type="checkbox">` to the shared `Checkbox` primitive,
            same as the header select-all above it (see that migration's
            comment for the paint mismatch this fixes). Keeps the exact
            `onClick`/no-op-`onChange` split this row already had —
            `onToggleSelect` needs the raw `MouseEvent` for `event.shiftKey`
            (shift-click range-select), which `Checkbox`'s own `onChange`
            can't carry (see `Checkbox`'s new `onClick` prop doc comment). */}
        <Checkbox
          checked={selected}
          onClick={onToggleSelect}
          onChange={() => {}}
          label={<span className="sr-only">Select {part.name}</span>}
        />
      </td>
      <td>
        {part.thumbnailUrl ? (
          // Render as an <img>: React escapes the src attribute, so there's no
          // CSS `url()` context to break out of (a server-supplied thumbnailUrl
          // can't inject styles). Decorative — the row name carries the label.
          <img className="staging-row-thumb" src={part.thumbnailUrl} alt="" aria-hidden="true" />
        ) : (
          <span className="staging-row-thumb staging-row-thumb-placeholder" aria-hidden="true" />
        )}
      </td>
      <td>
        <div className="parts-row-name">
          <span className="parts-row-name-text" title={part.name}>
            {part.name}
          </span>
          {/* ADR-0130 (Bug 4 item 6) — the CAD filename is its OWN line
              (`Fictiv Platform.dc.html:3286-3289`). Optional/additive field;
              absent ⇒ no line (never prints "undefined"). UID-558 — the
              associated-drawing filename that used to render below this is
              removed: it duplicated the DrawingAssociationControl cell one
              column over. */}
          {part.cadFilename && (
            <span className="caption-text parts-row-cad-filename">
              <AppstoreOutlined aria-hidden="true" /> {part.cadFilename}
            </span>
          )}
          {/* UID-559 — on the triage page only, suppress the "Needs
              configuration" reason chip below the part name; every other
              reason's chip is unchanged. */}
          {analysisSignal && analysisSignal.reason !== 'needs-configuration' && (
            <Chip
              tone={analysisSignal.disposition === 'auto-quote' ? 'success' : 'warning'}
              className="staging-row-signal-chip"
              title={ANALYSIS_SIGNAL_REASON_LABEL[analysisSignal.reason]}
            >
              {ANALYSIS_SIGNAL_REASON_LABEL[analysisSignal.reason]}
            </Chip>
          )}
        </div>
      </td>
      <td>
        <DrawingAssociationControl
          partName={part.name}
          drawingFilename={part.drawingFilename}
          candidateDrawings={candidateDrawings}
          onAssociateDrawing={onAssociateDrawing}
          onGenerateDrawing={onGenerateDrawing}
          onUploadDrawing={onUploadDrawing}
          emptyLabel="Add a drawing"
          menuLabel={`Associated drawing for ${part.name}`}
          // UID-560 — this column ("Drawing / matched parts") wants a
          // consistent pill width per row, not shrink-to-fit.
          fullWidth
        />
      </td>
      <td>
        <Chip tone={PART_TYPE_TONE[part.type]}>{STAGED_PART_TYPE_LABEL[part.type]}</Chip>
        {part.configuration && (
          <p className="caption-text staging-row-process">{part.configuration.process}</p>
        )}
      </td>
      <td>
        {/* ADR-0079 OQ-6 — CONFIGURATION STATUS is derived client-side from
            the existing `configuration` field; no new BFF enum. Additive
            cell (does not remove/replace any existing action). */}
        <Chip tone={part.configuration !== null ? 'success' : 'neutral'}>
          {part.configuration !== null ? 'Configured' : 'Not configured'}
        </Chip>
      </td>
      {/* UID-402 — the "Set material…" MATERIAL cell was removed. `material` is
          a `StagedConfiguration` attribute, so setting it from Triage was
          configuration, which this page must not offer. It was also a second
          amber source on the row (the chip went `tone="warning"` whenever
          material was unset), independent of anything needing resolution.
          Material is set where configuration lives: the Part Library and the
          RFQ compose screen. See ADR-0124. */}
      <td>
        {/* ADR-0130 (Bug 4 item 5) — plain numeric input, matching the design
            (`Fictiv Platform.dc.html:3327`), replacing the preset-picker
            DropdownMenu the control's shape had never been checked against.
            Qty stays an editable review field (ADR-0124's ruling on Qty
            stands unchanged) — only the control shape changed. */}
        <label className="sr-only" htmlFor={`${fileInputId}-qty`}>
          Quantity for {part.name}
        </label>
        <input
          id={`${fileInputId}-qty`}
          inputMode="numeric"
          className="staging-qty-input staging-bom-qty-input"
          value={qtyDraft}
          onChange={handleQtyChange}
          onBlur={commitQuantity}
        />
      </td>
      <td>
        <button
          type="button"
          className="staging-row-remove"
          aria-label={`Remove ${part.name}`}
          onClick={onRemove}
        >
          <DeleteOutlined aria-hidden="true" />
        </button>
      </td>
    </tr>
  )
}
